import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { PlusCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import DashboardSidebar from './components/DashboardSidebar';
import DashboardStatsCards from './components/DashboardStatsCards';
import WeddingTable from './components/WeddingTable';
import CreateWeddingModal from './components/CreateWeddingModal';
import AccountTab from './components/AccountTab';

const MyWeddings = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('weddings');
  const [weddings, setWeddings] = useState([]);
  const [stats, setStats] = useState(null);
  const [planId, setPlanId] = useState('free');
  const [weddingLimit, setWeddingLimit] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await api.listWeddings(token);
      setWeddings(data.weddings || []);
      setStats(data.stats || null);
      setPlanId(data.plan_id || 'free');
      setWeddingLimit(data.wedding_limit ?? 1);
    } catch (err) {
      console.error('Failed to load weddings:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const weddingCount = stats?.total_weddings || weddings.length;

  return (
    <>
      <Helmet>
        <title>My Weddings — Our Day</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;800&family=Lato:wght@300;400;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div
        className="min-h-screen flex overflow-hidden bg-[#FDFCF5] text-[#36454F] antialiased"
        style={{ fontFamily: "'Lato', sans-serif" }}
      >
        <DashboardSidebar activeTab={activeTab} onTabChange={setActiveTab} />

        <main className="flex-1 overflow-y-auto h-screen p-8 md:p-12 relative">
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#F3E5AB]/20 via-[#D4AF37]/5 to-transparent rounded-bl-full pointer-events-none" />

          <div className="max-w-7xl mx-auto space-y-8 relative z-10">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 pb-6 border-b border-[#D4AF37]/20">
              <div className="space-y-3">
                <h2
                  className="text-3xl md:text-4xl text-[#36454F] font-bold"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  {activeTab === 'weddings' ? 'My Weddings' : 'Account Settings'}
                </h2>
                <p className="text-[#54626F] text-base font-light tracking-wide">
                  {activeTab === 'weddings'
                    ? 'Manage your wedding invitations and track RSVP responses.'
                    : 'Update your profile and change your password.'}
                </p>
              </div>
              {activeTab === 'weddings' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 rounded shadow-lg flex items-center gap-2 uppercase text-xs tracking-widest font-bold text-white transition-transform active:scale-95"
                  style={{
                    background: 'linear-gradient(135deg, #E5C56A 0%, #C5A059 100%)',
                    border: '1px solid #B8860B',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  <PlusCircle className="w-4 h-4" />
                  New Wedding
                </button>
              )}
            </header>

            {/* Stats Cards (weddings tab only) */}
            {activeTab === 'weddings' && (
              loading ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-28 rounded-sm bg-white animate-pulse"
                      style={{ border: '1px solid rgba(212,175,55,0.2)' }}
                    />
                  ))}
                </div>
              ) : (
                <DashboardStatsCards
                  stats={stats}
                  weddingCount={weddingCount}
                  weddingLimit={weddingLimit}
                />
              )
            )}

            {/* Tab Content */}
            {activeTab === 'weddings' && (
              <WeddingTable weddings={weddings} loading={loading} onRefresh={fetchData} />
            )}
            {activeTab === 'account' && <AccountTab />}
          </div>

          {/* Footer */}
          <div className="text-center pt-12 pb-4 opacity-70">
            <div className="w-12 h-[1px] bg-[#D4AF37] mx-auto mb-4" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#54626F]">
              Our Day &copy; 2026. Elegance in every detail.
            </p>
          </div>
        </main>
      </div>

      <CreateWeddingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={fetchData}
        weddingCount={weddingCount}
        weddingLimit={weddingLimit}
      />
    </>
  );
};

export default MyWeddings;
